import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import DisplayConnectLinks from './DisplayConnectLinks';
import { supabase } from '@/integrations/supabase/client';

interface SocialLinks {
  tiktok?: string;
  facebook?: string;
  website?: string;
  youtube?: string;
  instagram?: string;
}

interface DisplayOwnerCardProps {
  avatarUrl: string;
  ownerName: string;
  ownerTitle?: string;
  ownerId: string;
  facebook?: string | null;
  website?: string | null;
  className?: string;
}

const DisplayOwnerCard: React.FC<DisplayOwnerCardProps> = ({
  avatarUrl,
  ownerName,
  ownerTitle = "Display Creator",
  ownerId,
  facebook,
  website,
  className
}) => {
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({});
  const [isLoading, setIsLoading] = useState(true);
  const profileUrl = `/owner/${ownerId}`;

  // Fetch user profile data including social links
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!ownerId) {
        setIsLoading(false);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('social_links')
          .eq('user_id', ownerId)
          .single();

        if (error) {
          console.error('Error fetching user profile:', error);
        } else if (profile?.social_links) {
          setSocialLinks(profile.social_links);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [ownerId]);

  return (
    <Card className={cn('group transition-all duration-200 hover:shadow-md', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Display Owner</CardTitle>
      </CardHeader>
      <CardContent>
        <Link to={profileUrl} className="block">
          <div className="flex items-center space-x-4">
            <Avatar className="transition-transform duration-200 group-hover:scale-105">
              <AvatarImage src={avatarUrl} alt={ownerName} />
              <AvatarFallback>{ownerName.charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium group-hover:text-primary transition-colors">
                {ownerName}
              </div>
              <div className="text-sm text-muted-foreground">{ownerTitle}</div>
            </div>
          </div>
        </Link>
        
        {/* Connect Section */}
        {!isLoading && (Object.keys(socialLinks).length > 0 || facebook || website) && (
          <div className="mt-4">
            <h4 className="text-sm font-medium mb-2">Connect</h4>
            <DisplayConnectLinks
              facebook={facebook}
              website={website}
              socialLinks={socialLinks}
              variant="icons"
            />
          </div>
        )}
        
        <Button 
          className="w-full mt-4 opacity-80 group-hover:opacity-100 transition-opacity" 
          variant="outline"
          asChild
        >
          <Link to={profileUrl}>
            View Profile
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};

export default DisplayOwnerCard;
